/* eslint-disable office-addins/no-context-sync-in-loop */
/* global Word console */

import { LIST_NUMBER_STYLES } from "../consts";
import { ParseState } from "../types";

/**
 * Tries to apply a list style from a list of alternatives.
 * Returns true if a style was successfully applied, false otherwise.
 */
export async function tryApplyListStyle(
  paragraph: Word.Paragraph,
  styleList: string[],
  context?: Word.RequestContext
): Promise<boolean> {
  for (const styleName of styleList) {
    try {
      paragraph.style = styleName;
      if (context) {
        await context.sync();
      }
      return true;
    } catch {
      continue;
    }
  }
  return false;
}

/**
 * Sets up a numbered list on a paragraph with the specified starting number.
 * Creates a new list to ensure correct numbering regardless of document context.
 */
export async function setupNumberedList(
  paragraph: Word.Paragraph,
  level: number,
  startingNumber: number,
  context?: Word.RequestContext
): Promise<{ success: boolean; listId?: number }> {
  try {
    // Apply numbered list style
    const styleApplied = await tryApplyListStyle(paragraph, LIST_NUMBER_STYLES, context);
    if (!styleApplied) {
      paragraph.styleBuiltIn = Word.BuiltInStyleName.listParagraph;
    }
    if (context) await context.sync();

    // Detach from any auto-attached list
    try {
      paragraph.detachFromList();
      if (context) await context.sync();
    } catch {
      /* Expected if not in a list */
    }

    // Start a new list
    paragraph.startNewList();
    if (context) await context.sync();

    // Configure the list
    const list = paragraph.list;
    if (!list || !context) {
      return { success: false };
    }

    context.load(list, "id");
    await context.sync();

    // Set Arabic numbering format with period (e.g., "1.", "2.", "3.")
    list.setLevelNumbering(level, Word.ListNumbering.arabic, [level, "."]);
    await context.sync();

    list.setLevelStartingNumber(level, startingNumber);
    await context.sync();

    return { success: true, listId: list.id };
  } catch (e) {
    console.warn("Could not set up numbered list:", e);
    return { success: false };
  }
}

export function parseListStartNumber(
  prefix: string | null | undefined,
  listType: string | null | undefined
): number | undefined {
  if (!prefix || listType === "bullet") {
    return undefined;
  }

  const trimmedPrefix = prefix.trim();
  if (!trimmedPrefix || trimmedPrefix.startsWith("-")) {
    return undefined;
  }

  // Try to parse decimal number (e.g., "1.", "2.")
  const decimalMatch = trimmedPrefix.match(/^(\d+)[.)\t]/);
  if (decimalMatch) {
    return parseInt(decimalMatch[1], 10);
  }

  // Try to parse lowercase letter (e.g., "a)", "b)")
  const letterMatch = trimmedPrefix.match(/^([a-z])[.)\t]/);
  if (letterMatch) {
    return letterMatch[1].charCodeAt(0) - "a".charCodeAt(0) + 1;
  }

  // Try to parse uppercase letter (e.g., "A)", "B)")
  const upperLetterMatch = trimmedPrefix.match(/^([A-Z])[.)\t]/);
  if (upperLetterMatch) {
    return upperLetterMatch[1].charCodeAt(0) - "A".charCodeAt(0) + 1;
  }

  // Try to parse roman numerals (lowercase: i, ii, iii, iv, etc.)
  const romanMatch = trimmedPrefix.match(/^([ivxlcdm]+)[.)\t]/i);
  if (romanMatch) {
    return romanToInt(romanMatch[1].toUpperCase());
  }

  return undefined;
}

/**
 * Convert a Roman numeral string to an integer.
 */
function romanToInt(s: string): number {
  const romanValues: Record<string, number> = {
    I: 1,
    V: 5,
    X: 10,
    L: 50,
    C: 100,
    D: 500,
    M: 1000,
  };
  let result = 0;
  let prevValue = 0;

  for (let i = s.length - 1; i >= 0; i--) {
    const value = romanValues[s[i].toUpperCase()] || 0;
    if (value < prevValue) {
      result -= value;
    } else {
      result += value;
    }
    prevValue = value;
  }

  return result;
}

export function pruneListState(state: ParseState, currentLevel: number): void {
  const toRemove = Object.keys(state.listState)
    .map(Number)
    .filter((level) => level > currentLevel);
  for (const level of toRemove) {
    delete state.listState[level];
  }
}
